import React from 'react';
import {
  Box,
  Typography,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Header } from 'components/home';
import { TopNavBar } from 'components/top_nav_bar';
import { ContainerForm } from 'components/shared/container_form';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import { useFontesRendaFamilia } from './useFontesRendaFamilia';
import { CuffaIncomeSourceEnum } from 'interfaces/cuffa.interface';

const fontesEnum = Object.values(CuffaIncomeSourceEnum);
const outroValorFixo = 'Outro';
const fontesPadrao = [...fontesEnum, outroValorFixo];

export const FontesRendaFamilia: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();

  const {
    formik,
    initialLoading,
    saving,
    selecionadas,
    ordens,
    openSuccessSnackbar,
    openErrorSnackbar,
    snackbarMessage,
    handleCloseSnackbar,
    handleSelecionar,
    handleOrderChange,
    handleSave,
    handleAdvance,
  } = useFontesRendaFamilia();

  if (initialLoading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Fontes de Renda da Família" showBackButton />
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress />
          <Typography ml={2}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Fontes de Renda da Família" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Fontes de Renda" width="100%">
            <form onSubmit={formik.handleSubmit}>
              <Box mb={3}>
                <Typography fontWeight="bold" mb={1}>
                  Marque todas as fontes de renda presentes na família e ordene conforme a importância:
                </Typography>
                <Typography fontSize="14px">
                  A prioridade será preenchida automaticamente. Se necessário, você pode ajustá-la.
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                {fontesPadrao.map((nome) => (
                  <Box key={nome} display="flex" justifyContent="space-between" alignItems="center" sx={{ paddingBottom: 2 }}>
                    <Box display="flex" alignItems="center" gap={2} sx={{ flex: 1 }}>
                      <Checkbox
                        checked={selecionadas.includes(nome)}
                        onChange={() => handleSelecionar(nome)}
                      />
                      <Typography>{nome}</Typography>
                    </Box>

                    {selecionadas.includes(nome) && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontSize="14px">Prioridade:</Typography>
                        <Select
                          size="small"
                          sx={{ width: 80 }}
                          value={ordens.find((o) => o.value === nome)?.order ?? ''}
                          onChange={(e) => handleOrderChange(nome, Number(e.target.value))}
                        >
                          {[...Array(selecionadas.length)].map((_, i) => (
                            <MenuItem key={i + 1} value={i + 1}>
                              {i + 1}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>

              {selecionadas.includes(outroValorFixo) && (
                <Box mt={2}>
                  <TextField
                    name="outroTexto"
                    value={formik.values.outroTexto}
                    onChange={(e) => formik.setFieldValue("outroTexto", e.target.value.toUpperCase())}
                    onBlur={formik.handleBlur}
                    placeholder="Descreva a outra fonte de renda"
                    fullWidth
                    sx={{ background: '#fff', borderRadius: 1 }}
                  />
                </Box>
              )}

              <Box mt={4} display="flex" justifyContent="center" gap={2}>
                <Button variant="outlined" color="secondary" onClick={() => navigate(ROUTES.CREDITO_FINANCIAMENTO_CUFFA_INCLUIR.replace(':uuid', uuid ?? ''))} disabled={saving}>
                  Retornar
                </Button>
                <Button variant="outlined" color="secondary" onClick={handleSave} disabled={saving}>
                  {saving ? <CircularProgress size={24} color="inherit" /> : 'Salvar'}
                </Button>
                <Button variant="contained" color="primary" onClick={handleAdvance} disabled={saving}>
                  {saving && <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />}
                  Avançar
                </Button>
              </Box>
            </form>
          </ContainerForm>
        </Box>
      </Box>

      <Snackbar open={openSuccessSnackbar} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </MenuProvider>
  );
};
